import {useEffect, useRef, useState} from 'react';

import {SMART_SEARCH_KEY} from '../constants/smartSearchKey';
import type {EagGridStore} from '../store/createEagGridStore';
import type {GetDataQueryResponse} from '../types/api/data';
import type {Filters} from '../types/Filters';
import type {QueryFilterObject} from '../types/QueryFilterObject';
import {isOnlyOrderChanged} from '../utils/isOnlyOrderChanged';
import {normalizeFilterValues} from '../utils/normalizeFilterValues';

export type UseFilterHydrationParams = {
  dataQuery: GetDataQueryResponse | null | undefined;
  queryModifier: ((filter: QueryFilterObject) => QueryFilterObject) | undefined;
  initialSmartSearch: string | undefined;
  store: Pick<EagGridStore, 'setStaticFilters' | 'setFilterValues'>;
};

/**
 * Watches `dataQuery` and re-seeds `filterValues` in the store whenever the
 * server delivers a new data query (initial load, preset switch, query-id fallback).
 *
 * Two guards prevent spurious re-seeding:
 *   Guard 1 — exact same object reference: no-op (React double-invoke safety).
 *   Guard 2 — only `order` changed (`setColumnSort` preserves `.filters` reference):
 *             skips `setFilterValues` to avoid a duplicate `createDataQuery` POST.
 *
 * Returns `isFilterHydrated` — becomes `true` after the first successful seed.
 */
export function useFilterHydration(params: UseFilterHydrationParams): boolean {
  const [isFilterHydrated, setIsFilterHydrated] = useState(false);
  const prevDataQueryRef = useRef<GetDataQueryResponse | null>(null);

  // eslint-disable-next-line no-restricted-syntax -- intentional: reactive sync; re-seeds filter values in store whenever dataQuery changes (initial load + every preset switch); prev-ref guard prevents double-run
  useEffect(() => {
    if (!params.dataQuery) {
      return;
    }

    const prevDataQuery = prevDataQueryRef.current;

    // Guard 1: exact same object reference — no change at all.
    if (prevDataQuery === params.dataQuery) {
      return;
    }

    prevDataQueryRef.current = params.dataQuery;

    // Guard 2: only dataQuery.order changed (setColumnSort spreads state.dataQuery
    // preserving the .filters reference). Re-initializing filterValues here would
    // trigger a spurious fetchPage → duplicate data-query POST in the network.
    if (isOnlyOrderChanged(prevDataQuery, params.dataQuery)) {
      return;
    }

    const staticFilters = params.queryModifier ? params.queryModifier({}) : {};
    const smartSearchValue = params.initialSmartSearch ?? params.dataQuery.smartSearch;
    // normalizeFilterValues handles both BE response formats:
    //   flat:    { key: value }           → wraps to { key: { value: value } }
    //   wrapped: { key: { value: value } } → passes through unchanged
    const newInitialFilters: Filters = {
      ...normalizeFilterValues(params.dataQuery.filters),
      ...(smartSearchValue ? {[SMART_SEARCH_KEY]: {[SMART_SEARCH_KEY]: smartSearchValue}} : {}),
    };

    params.store.setStaticFilters(staticFilters);
    params.store.setFilterValues(newInitialFilters);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: single false→true transition marking hydration complete; batched with preceding store mutations in React 18
    setIsFilterHydrated(true);
  }, [params.dataQuery, params.queryModifier, params.initialSmartSearch, params.store]);

  return isFilterHydrated;
}
